import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DOPPLER_CONFIG } from '../config/dopplerRadar.config.js';
import {
  clearDirectTileFailure,
  createDirectTileFailureState,
  DOPPLER_TILE_SOURCE_MODES,
  getDirectFailureFallbackReason,
  registerDirectTileFailure,
  shouldFallbackToProxy,
} from '../utils/dopplerTileSource.utils.js';
import {
  canUseFusionProxyTiles,
  logDirectTileFallbackSwitch,
  resolveTileSourceMode,
} from '../utils/dopplerTile.utils.js';

// Tracks direct tile failures and switches the whole radar to the backend
// proxy when the configured failure limits are reached.
export default function useDopplerTileSource({ onBeforeSourceChange } = {}) {
  const [tileSourceMode, setTileSourceMode] = useState(null);
  const resolvedTileSourceMode = useMemo(
    () => resolveTileSourceMode(tileSourceMode),
    [tileSourceMode],
  );
  const resolvedTileSourceModeRef = useRef(resolvedTileSourceMode);
  const directTileFailureStateRef = useRef(createDirectTileFailureState());

  const resetDirectTileFailures = useCallback(() => {
    directTileFailureStateRef.current = createDirectTileFailureState();
  }, []);

  const markDirectFrameRecovered = useCallback((frameIndex) => {
    if (resolvedTileSourceModeRef.current === DOPPLER_TILE_SOURCE_MODES.direct) {
      clearDirectTileFailure(directTileFailureStateRef.current, frameIndex);
    }
  }, []);

  const maybeSwitchToProxy = useCallback(
    (frameIndex) => {
      if (resolvedTileSourceModeRef.current !== DOPPLER_TILE_SOURCE_MODES.direct) {
        return false;
      }

      const failureState = registerDirectTileFailure(directTileFailureStateRef.current, frameIndex);
      const isProxyAvailable = canUseFusionProxyTiles();
      const shouldSwitch = shouldFallbackToProxy({
        tileSourceMode: resolvedTileSourceModeRef.current,
        proxyFallbackEnabled: isProxyAvailable,
        directFailureFallbackEnabled: DOPPLER_CONFIG.directFailureFallbackEnabled,
        totalTileErrorCount: failureState.totalTileErrors,
        failedFrameCount: failureState.failedFrames.size,
        tileErrorThreshold: DOPPLER_CONFIG.directFailureFallbackTileErrorThreshold,
        frameThreshold: DOPPLER_CONFIG.directFailureFallbackFrameThreshold,
      });

      if (!shouldSwitch || !isProxyAvailable) {
        return false;
      }

      onBeforeSourceChange?.();
      resolvedTileSourceModeRef.current = DOPPLER_TILE_SOURCE_MODES.proxy;
      logDirectTileFallbackSwitch(
        getDirectFailureFallbackReason({
          totalTileErrorCount: failureState.totalTileErrors,
          failedFrameCount: failureState.failedFrames.size,
        }),
      );
      setTileSourceMode(DOPPLER_TILE_SOURCE_MODES.proxy);
      return true;
    },
    [onBeforeSourceChange],
  );

  useEffect(() => {
    resolvedTileSourceModeRef.current = resolvedTileSourceMode;
    if (resolvedTileSourceMode !== DOPPLER_TILE_SOURCE_MODES.direct) {
      resetDirectTileFailures();
    }
  }, [resetDirectTileFailures, resolvedTileSourceMode]);

  return {
    tileSourceMode,
    resolvedTileSourceMode,
    resetDirectTileFailures,
    markDirectFrameRecovered,
    maybeSwitchToProxy,
  };
}
